import { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Bar, BarChart, Cell, LabelList, Line, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import { SegToggle } from "@plannel/components/dashboard/DashboardToggle";
import DashboardLayoutModal from "@plannel/components/dashboard/DashboardLayoutModal";
import ChartConfigModal from "@plannel/components/modal/ChartConfigModal";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import { DashboardComposedChart, barOrLine, areaGradientDefs, DW_CHART_TYPES } from "@plannel/components/dashboard/DashboardChart";
import { gridLine, axisTick, tooltipProps } from "@plannel/components/dashboard/chartTokens";
import {
  AgStyleTooltipContent, AgTooltipBox, AgTooltipRow, AG_TOOLTIP_HEADING_STYLE,
} from "@plannel/components/rechart/AgStyleTooltip";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n, { MAX_TOP_N } from "@plannel/hooks/useDashboardP13n";
import ColorUtil from "@plannel/pages/utils/color-util";
import salesPlanAccuracyService from "@plannel/services/dashboard/sales-plan-accuracy-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import codeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";

/*
 * Sales Plan Accuracy Dashboard — DP 계획이 실제 판매를 얼마나 맞혔는가(계획 품질).
 * 소스/산식 명세: saas-application/docs/sql/dw/dashboard-spec/04_sales_plan_accuracy.md
 *
 * 화면 전체가 실데이터다(목업 없음) — 서비스는 `/api/dashboard-dw/sales-plan-accuracy` 호출 한 줄이고
 * 백엔드 3종(Controller/Service/JdbcQueryService)이 다 있다. **DB 실행 검증만 미완.**
 *
 * ★ 비교 기준은 **MF(매니저 수요계획) 고정** — 달성률·정확도·편향·갭의 분모가 전부 `mf_*` 다.
 *   생산·구매가 실제로 쓰는 숫자이고 홈 "계획 대비 실적" 카드와 기준이 같아야 화면 간 숫자가 맞는다.
 *   FF/SF/MF/BF 단계 비교는 **Accuracy by Plan Stage 카드 한 장뿐**이다.
 *
 * 화면 이름을 목업("Sales Plan vs Actual")에서 바꿨다. 축이 비교가 아니라 **정확도·편향**이고,
 * 덤으로 목업 leaf(salesPlanVsActualDashboard)와 이름이 겹치지 않는다 — 그래서 "2" 접미사가 없다.
 *
 * 기간이 두 개다: KPI 는 **완료 12개월**(당월 제외 — 부분 월이라 달성률이 구조적으로 낮다),
 * 차트는 **16개월**(과거 12 + 당월 + 미래 3). 미래 구간은 계획만 있고 실적·달성률은 null 이다.
 */

// 카드 정의 — span(6열 기준)·행 높이. minH 는 minmax(minH px, minH fr) 로 "최소 높이 겸 세로 분배 가중치".
const CARD_DEFS = [
  { key: "trend", span: 4, minH: 300 },
  { key: "stage", span: 2, minH: 300 },
  { key: "dist", span: 2, minH: 260 },
  { key: "deviation", span: 4, minH: 280 },
  { key: "detail", span: 6, minH: 380 },
];
const DEFAULT_LAYOUT = { order: CARD_DEFS.map((c) => c.key), hidden: {} };

/*
 * 시리즈별 차트 타입·표시 기본값(z_p13n config_obj.chart, 사용자별).
 * 차트 2장이 이 한 객체를 공유하므로 시리즈 key 는 페이지 안에서 유일해야 한다.
 *   FF(재무계획)는 "기준선" 성격이라 DASHED — 계획 3종이 전부 막대면 어느 게 기준인지 안 보인다.
 *   단계별 정확도는 추세 비교라 전부 라인, BF 만 DASHED(사람 계획이 아닌 기계 기준선).
 */
const DEFAULT_CHART_CONFIG = {
  // 계획 vs 실적 추이
  ff: { chartType: "DASHED", activeFlg: true },
  sf: { chartType: "BAR", activeFlg: true },
  mf: { chartType: "BAR", activeFlg: true },
  actual: { chartType: "BAR", activeFlg: true },
  attainment: { chartType: "LINE", activeFlg: true },
  // 계획 단계별 정확도
  ffAcc: { chartType: "LINE", activeFlg: true },
  sfAcc: { chartType: "LINE", activeFlg: true },
  mfAcc: { chartType: "LINE", activeFlg: true },
  bfAcc: { chartType: "DASHED", activeFlg: true },
};

/*
 * 시리즈 팔레트 인덱스 — 차트 색과 설정 모달 스와치가 공유한다(색 불일치 방지).
 * ★ 같은 계획 단계는 두 카드에서 같은 색을 쓴다(ff=14, sf=3, mf=0) — 추이 카드의 MF 막대와
 *   정확도 카드의 MF 라인이 다른 색이면 같은 계획이라는 게 안 읽힌다.
 */
const STAGE_COLOR_IDX = { ff: 14, sf: 3, mf: 0, bf: 4 };
const TREND_COLOR_IDX = { actual: 2, attainment: 1 };

/*
 * 계획·실적 시리즈 색 — 추이 카드(①)와 히트맵 펼침 패널(⑤)이 공유한다.
 * 렌더 시점에 부르는 함수인 이유: 모듈 상수로 굳히면 사용자 팔레트가 로드되기 전 값으로 고정된다.
 */
const planSeriesColors = () => ({
  ff: ColorUtil.colorSet(STAGE_COLOR_IDX.ff),
  sf: ColorUtil.colorSet(STAGE_COLOR_IDX.sf),
  mf: ColorUtil.colorSet(STAGE_COLOR_IDX.mf),
  actual: ColorUtil.colorSet(TREND_COLOR_IDX.actual),
  attainment: ColorUtil.colorSet(TREND_COLOR_IDX.attainment),
});

// 달성률 분포 버킷 — 순서 고정(구간 축). 라벨이 숫자 구간인 것은 번역 대상이 아니다.
const DIST_BUCKETS = [
  { key: "noPlan", labelKey: "pvaNoPlan", tone: "muted" },
  { key: "under70", label: "< 70%", tone: "under2" },
  { key: "b70_90", label: "70 ~ 90%", tone: "under1" },
  { key: "b90_110", label: "90 ~ 110%", tone: "normal" },
  { key: "b110_130", label: "110 ~ 130%", tone: "over1" },
  { key: "over130", label: "> 130%", tone: "over2" },
  { key: "noSales", labelKey: "pvaNoSales", tone: "muted" },
];

/*
 * 달성률 색 스케일 — **분포 카드(③)와 히트맵(⑤)이 이 하나를 공유한다.**
 * 두 카드가 같은 구간을 다른 색으로 칠하면 사용자는 둘을 다른 지표로 읽는다.
 *
 * 발산형(diverging): 미달(빨강) ← 정상(초록) → 초과(파랑). 진하기 = 편차 크기.
 *   ★ 미달과 초과를 같은 "경고색"으로 칠하면 안 된다 — 과잉계획(재고 누적)과
 *     과소계획(결품)은 대응이 정반대라 방향이 색으로 보여야 한다.
 */
const toneColorOf = (theme) => ({
  under2: { color: theme.palette.error.main, alpha: 0.42 },
  under1: { color: theme.palette.error.main, alpha: 0.18 },
  normal: { color: theme.palette.success.main, alpha: 0.22 },
  over1: { color: theme.palette.info.main, alpha: 0.18 },
  over2: { color: theme.palette.info.main, alpha: 0.42 },
  muted: { color: theme.palette.text.disabled, alpha: 0.16 },
});

// 달성률 값 → 구간 key. null(미완료 월/미래)은 구간이 없다.
const bandOf = (v) => {
  if (v == null) return null;
  if (v < 70) return "under70";
  if (v < 90) return "b70_90";
  if (v <= 110) return "b90_110";
  if (v <= 130) return "b110_130";
  return "over130";
};
const TONE_BY_BUCKET = Object.fromEntries(DIST_BUCKETS.map((b) => [b.key, b.tone]));

// ─────────────────────────────────────────
// ① 계획 vs 실적 추이 — FF/SF/MF/실적 + 달성률(우측 %축)
// ─────────────────────────────────────────
const TrendCard = ({ title, data, fmtBy, chartConfig, onOpenConfig, onToggleSeries, onDrill, onDrillHover }) => {
  const { t } = useTranslation();
  const attainLabel = t("pvaAttainment");

  const SERIES = useMemo(() => planSeriesColors(), []);

  const legend = useMemo(() => [
    { key: "ff", label: t("financeForecast"), color: SERIES.ff },
    { key: "sf", label: t("salesForecast"), color: SERIES.sf },
    { key: "mf", label: t("managerForecast"), color: SERIES.mf },
    { key: "actual", label: t("salesActual"), color: SERIES.actual },
    { key: "attainment", label: `${attainLabel} (%)`, color: SERIES.attainment },
  ], [t, attainLabel, SERIES]);

  // 시리즈 표시 = 설정 activeFlg. 범례 체크박스와 설정 모달이 같은 소스를 공유해 자동 동기화.
  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;

  return (
    <>
      {/* 이 카드가 곧 "계획 대비 실적" → 그 상세 리포트로 드릴. 클릭 영역은 제목만(차트·범례와 충돌 방지) */}
      <DashboardCardHeader title={title} onOpenConfig={onOpenConfig}
        onDrill={onDrill} drillTitle={t("menuDpPlanVsActual")} onDrillHover={onDrillHover} />
      <DashboardComposedChart
        data={data}
        yAxes={[
          { id: "val", tickFormatter: fmtBy },
          { id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` },
        ]}
        tooltipFormatter={(v, name) => (name === `${attainLabel} (%)` ? `${v}%` : fmtBy(v))}
      >
        {/* AREA 로 바꿀 수 있는 시리즈는 전부 등록 — 빠지면 면이 검게 칠해진다 */}
        {areaGradientDefs([
          { dataKey: "ff", color: SERIES.ff },
          { dataKey: "sf", color: SERIES.sf },
          { dataKey: "mf", color: SERIES.mf },
          { dataKey: "actual", color: SERIES.actual },
          { dataKey: "attainment", color: SERIES.attainment },
        ])}
        {isActive("sf") && barOrLine(chartConfig, { key: "sf", dataKey: "sf", name: t("salesForecast"), color: SERIES.sf, yAxisId: "val", dflt: "BAR" })}
        {isActive("mf") && barOrLine(chartConfig, { key: "mf", dataKey: "mf", name: t("managerForecast"), color: SERIES.mf, yAxisId: "val", dflt: "BAR" })}
        {isActive("actual") && barOrLine(chartConfig, { key: "actual", dataKey: "actual", name: t("salesActual"), color: SERIES.actual, yAxisId: "val", dflt: "BAR" })}
        {isActive("ff") && barOrLine(chartConfig, { key: "ff", dataKey: "ff", name: t("financeForecast"), color: SERIES.ff, yAxisId: "val", dflt: "DASHED" })}
        {isActive("attainment") && barOrLine(chartConfig, { key: "attainment", dataKey: "attainment", name: `${attainLabel} (%)`, color: SERIES.attainment, yAxisId: "pct", dflt: "LINE" })}
      </DashboardComposedChart>
      <SeriesLegend items={legend} isActive={isActive} onToggle={onToggleSeries} />
    </>
  );
};

// ─────────────────────────────────────────
// ② ⭐ 계획 단계별 정확도 — 같은 실적에 대고 FF/SF/MF/BF 의 WAPE 를 각각 잰다
// ─────────────────────────────────────────
const StageAccuracyCard = ({ title, data, unit, chartConfig, onOpenConfig, onToggleSeries }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const SERIES = useMemo(() => ({
    ffAcc: ColorUtil.colorSet(STAGE_COLOR_IDX.ff),
    sfAcc: ColorUtil.colorSet(STAGE_COLOR_IDX.sf),
    mfAcc: ColorUtil.colorSet(STAGE_COLOR_IDX.mf),
    bfAcc: ColorUtil.colorSet(STAGE_COLOR_IDX.bf),
  }), []);

  /*
   * 이 카드의 핵심 신호: MF(사람이 조정한 확정계획)가 BF(통계예측)보다 정확도가 낮으면
   * 사람의 조정이 예측을 악화시키고 있다는 뜻이다. 차트만으론 눈에 안 띄어 한 줄로 명시한다.
   */
  const avg = (key) => {
    const vals = data.map((d) => d[key]).filter((v) => v != null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };
  const mfAvg = avg("mfAcc");
  const bfAvg = avg("bfAcc");
  const mfWorse = mfAvg != null && bfAvg != null && mfAvg < bfAvg;

  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;

  /*
   * ★ 이 카드도 페이지 Amt/Qty 토글을 따른다 (2026-08-07). 이전엔 혼자 수량 고정이었다.
   *   FF(재무계획)는 금액으로 세우는 계획이라 수량으로 채점하면 구조적으로 0 이 나온다
   *   — 실측에서 FF 암묵단가가 매달 1,768 로 고정(= ff_qty 는 ff_amt 의 파생값)이었다.
   *   BF 만은 `bf_amt` 가 없어 금액 기준을 못 낸다 → AMT 모드에서 시리즈·범례에서 빼고 이유를 표기한다.
   */
  const isAmt = unit === "AMT";
  const stages = ["ffAcc", "sfAcc", "mfAcc", ...(isAmt ? [] : ["bfAcc"])];
  /*
   * ★ FF 라벨에 (총계) 를 붙인다 — FF 만 **월 총계 그레인**이고 나머지는 셀(품목×거래처) 단위 WAPE 다.
   *   잣대가 다르다는 걸 라벨에서 안 밝히면 "재무계획이 제일 정확하다"는 정반대 오해가 생긴다
   *   (총계 86~99% vs 셀 단위 MF 69%). 아래 각주와 한 세트다.
   */
  const labelOf = {
    ffAcc: `${t("financeForecast")} (${t("total")})`,
    sfAcc: t("salesForecast"),
    mfAcc: t("managerForecast"),
    bfAcc: t("baselineForecast"),
  };

  return (
    <>
      <DashboardCardHeader title={title} onOpenConfig={onOpenConfig}>
        {isAmt && (
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("pvaBfQtyOnly")}</Typography>
        )}
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        yAxes={[{ domain: [0, 100], tickFormatter: (v) => `${v}%` }]}
        tooltipFormatter={(v) => `${v}%`}
      >
        {areaGradientDefs(stages.map((k) => ({ dataKey: k, color: SERIES[k] })))}
        {stages.filter(isActive).map((k) => barOrLine(chartConfig, {
          key: k, dataKey: k, name: labelOf[k], color: SERIES[k], dflt: k === "bfAcc" ? "DASHED" : "LINE",
        }))}
      </DashboardComposedChart>
      <SeriesLegend
        items={stages.map((k) => ({ key: k, label: labelOf[k], color: SERIES[k] }))}
        isActive={isActive}
        onToggle={onToggleSeries}
      />
      {mfWorse && (
        <Typography sx={{ fontSize: FS.BODY, color: theme.palette.warning.main, mt: 0.25 }}>
          {t("pvaMfWorseThanBf")}
        </Typography>
      )}
      {/* FF 만 잣대가 다르다는 각주 — 라벨의 (총계) 와 한 세트다 */}
      {isActive("ffAcc") && (
        <Typography sx={{ fontSize: FS.CAPTION, color: "text.disabled", mt: 0.25 }}>
          {t("pvaFfTotalGrain")}
        </Typography>
      )}
    </>
  );
};

// ─────────────────────────────────────────
// ③ 달성률 분포 — 조합 단위 달성률을 구간별로. 평균 100% 가 상쇄의 결과인지 여기서 갈린다.
// ─────────────────────────────────────────
const AttainDistCard = ({ title, dist, fmtBy }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // 색은 히트맵(⑤)과 공유하는 발산형 스케일 — 막대는 불투명이라 alpha 없이 원색을 쓴다.
  const tone = toneColorOf(theme);

  const byKey = Object.fromEntries((dist?.buckets ?? []).map((b) => [b.key, b]));
  const data = DIST_BUCKETS.map((b) => ({
    key: b.key,
    label: b.labelKey ? t(b.labelKey) : b.label,
    count: byKey[b.key]?.count ?? 0,
    amt: byKey[b.key]?.amt ?? 0,
    color: tone[b.tone].color,
  }));

  return (
    <>
      <DashboardCardHeader title={title} dense>
        <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>
          {`${t("pvaNormalBand")} ${dist?.normalPct ?? 0}%`}
        </Typography>
      </DashboardCardHeader>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 4" stroke={gridLine(theme)} horizontal={false} />
            <XAxis type="number" tick={axisTick(theme)} allowDecimals={false} />
            <YAxis dataKey="label" type="category" tick={axisTick(theme)} width={72} />
            <Tooltip {...tooltipProps()} content={<AgStyleTooltipContent />}
              formatter={(v, n, p) => [`${v} (${fmtBy(p?.payload?.amt ?? 0)})`, n]} />
            <Bar dataKey="count" name={t("count")} radius={[0, 3, 3, 0]} barSize={14} isAnimationActive={false}>
              {data.map((d) => <Cell key={d.key} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// ④ 계획 편차 상위 N — 0 을 가운데 둔 발산형 막대
// ─────────────────────────────────────────

/*
 * 왜 표가 아니라 막대인가 (2026-08-05 재설계)
 * 이 카드가 답할 질문은 **"어느 게 가장 크게, 어느 방향으로 틀어졌나"** 다 — 크기 비교와 방향 판정.
 * 표로 주면 숫자 6~10줄을 읽고 머릿속에서 정렬해야 한다. 길이와 좌우 위치로 주면 즉시 읽힌다.
 * 계획·실적 절대값은 툴팁으로 내린다(막대에 병기하면 카드가 다시 숫자 벽이 된다).
 */

// 막대 끝 값 라벨 — 양수는 오른쪽, 음수는 왼쪽. LabelList 의 position prop 으로는 부호별 분기가 안 된다.
//   recharts 는 음수 막대도 x=왼쪽끝 / width=절대폭으로 준다(x+width 가 0 위치).
const GapValueLabel = ({ x, y, width, height, value, fmt, negColor, posColor }) => {
  const up = (value ?? 0) >= 0;
  return (
    <text
      x={up ? x + width + 5 : x - 5}
      y={y + height / 2}
      dy={3.5}
      textAnchor={up ? "start" : "end"}
      fontSize={FS.CAPTION}
      fontWeight={600}
      fill={up ? posColor : negColor}
    >
      {`${(value ?? 0) > 0 ? "+" : ""}${fmt(value ?? 0)}`}
    </text>
  );
};

// 툴팁 — 막대가 보여주지 않는 계획·실적 절대값과 달성률을 여기서 준다.
const DeviationTooltip = ({ active, payload, fmt, t, negColor, posColor }) => {
  const d = payload?.[0]?.payload;
  if (!active || !d) return null;
  const gap = d.gapAmt ?? 0;
  return (
    <AgTooltipBox>
      <div style={AG_TOOLTIP_HEADING_STYLE}>{d.cd ? `${d.cd} · ${d.name}` : d.name}</div>
      <AgTooltipRow label={t("plan")} value={fmt(d.planAmt ?? 0)} />
      <AgTooltipRow label={t("actual")} value={fmt(d.actualAmt ?? 0)} />
      <AgTooltipRow color={gap < 0 ? negColor : posColor} label={t("deviation")}
        value={`${gap > 0 ? "+" : ""}${fmt(gap)}`} />
      <AgTooltipRow label={t("pvaAttainment")} value={`${d.attainment ?? 0}%`} />
    </AgTooltipBox>
  );
};

/*
 * 선택한 엔티티의 월별 계획·실적 + 달성률.
 * 막대 하나를 월로 쪼갠 것이라 **월 합계가 좌측 막대 값과 맞는다**(서버가 같은 환율·같은 모집단으로 만든다).
 * 달성률은 계획 0 인 달 null → 선이 끊긴다(0% 로 잇지 않는다. 없는 값과 0 은 다르다).
 */
const DeviationDetail = ({ row, fmtBy }) => {
  const { t } = useTranslation();
  const series = row?.trend ?? [];
  // 색은 추이 카드와 공유한다 — 여기 계획은 MF 이므로 MF 막대와 같은 색이어야 같은 계획으로 읽힌다.
  const C = planSeriesColors();

  if (!row) return null;
  return (
    <Box sx={{ flex: "0 0 40%", minWidth: 0, display: "flex", flexDirection: "column", pl: 1.5, borderLeft: 1, borderColor: "divider" }}>
      <Box sx={{ flexShrink: 0, mb: 0.5 }}>
        <Typography noWrap sx={{ fontSize: FS.SUB, fontWeight: 600, color: "text.primary" }}>
          {row.cd ? `${row.cd} · ${row.name}` : row.name}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>
            {`${t("plan")} ${fmtBy(row.planAmt ?? 0)}`}
          </Typography>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>
            {`${t("actual")} ${fmtBy(row.actualAmt ?? 0)}`}
          </Typography>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>
            {`${t("pvaAttainment")} ${row.attainment ?? 0}%`}
          </Typography>
        </Box>
      </Box>
      {/* 월 시리즈가 비면 빈 차트 축만 남아 "고장난 카드"로 보인다 → 명시적 빈 상태.
          구버전 백엔드(trend 필드 이전)로도 이 경로를 탄다. */}
      {series.length === 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: FS.BODY, color: "text.disabled" }}>{t("MSG_NoData")}</Typography>
        </Box>
      ) : (
        /* ★ DashboardComposedChart 는 자기 안에 이미 `flex:1; minHeight:0` Box 를 갖고 있다.
           위에 일반 Box 를 한 겹 더 씌우면 그 래퍼가 display:block 이라 flex:1 이 죽고,
           높이가 auto 로 접혀 ResponsiveContainer(height="100%") 가 0 이 된다 → 차트가 안 보인다.
           **column flex 의 직접 자식으로 둘 것.** */
        <DashboardComposedChart
          data={series}
          xKey="ym"
          yAxes={[
            { id: "val", tickFormatter: fmtBy },
            { id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` },
          ]}
          tooltipFormatter={(v, name) => (name === `${t("pvaAttainment")} (%)` ? `${v}%` : fmtBy(v))}
        >
          <Bar dataKey="plan" name={t("plan")} fill={C.mf} yAxisId="val" barSize={9} isAnimationActive={false} />
          <Bar dataKey="actual" name={t("actual")} fill={C.actual} yAxisId="val" barSize={9} isAnimationActive={false} />
          <Line type="monotone" dataKey="attainment" name={`${t("pvaAttainment")} (%)`}
            stroke={C.attainment} strokeWidth={1.6} yAxisId="pct"
            dot={false} connectNulls={false} isAnimationActive={false} />
        </DashboardComposedChart>
      )}
    </Box>
  );
};

const DeviationChart = ({ title, rows, axis, onChangeAxis, fmtBy, topN, onChangeTopN }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  /*
   * 선택 = 상세 패널이 보여줄 행. 막대 클릭으로 바꾼다.
   * 축(품목/거래처)·단위·Top N 이 바뀌면 rows 자체가 갈리므로 **선택을 이름으로 다시 맞춘다** —
   * 인덱스로 들고 있으면 목록이 바뀔 때 엉뚱한 행이 열린다. 없으면 1위로 되돌린다(기본값).
   */
  const [selectedName, setSelectedName] = useState(null);
  const selected = rows.find((r) => r.name === selectedName) ?? rows[0] ?? null;
  // 히트맵·분포 카드와 같은 발산형 색 — 미달=빨강, 초과=파랑. 화면 전체가 한 규칙을 쓴다.
  const tone = useMemo(() => toneColorOf(theme), [theme]);
  const negColor = tone.under2.color;
  const posColor = tone.over2.color;

  /*
   * 0 이 정확히 가운데 오도록 도메인을 **대칭**으로 잡는다.
   * recharts 기본 도메인은 데이터 범위에 맞춰져, 미달만 많으면 0 이 오른쪽 끝으로 밀려
   * "다 미달"이 "좀 미달"처럼 보인다. 방향 비교가 목적인 차트에서 이건 왜곡이다.
   * 1.18 은 막대 끝 값 라벨이 그려질 여백.
   */
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.gapAmt ?? 0)));
  const bound = maxAbs * 1.18;

  const topNOptions = Array.from({ length: MAX_TOP_N }, (_, i) => ({ value: i + 1, label: `Top ${i + 1}` }));
  // 긴 품목명은 축에서 자르고 전체 이름은 툴팁 제목에서 보여준다
  const cutName = (v) => (String(v).length > 16 ? `${String(v).slice(0, 15)}…` : v);

  return (
    <>
      <DashboardCardHeader title={title} dense>
        <SegToggle value={axis} options={["item", "customer"]} onChange={onChangeAxis} />
        <CompactSelect value={topN} options={topNOptions} onChange={onChangeTopN} minWidth={78} />
      </DashboardCardHeader>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* 좌: 편차 순위(마스터) — 우측 상세에 자리를 내주느라 좁아졌다 */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 4" stroke={gridLine(theme)} horizontal={false} />
              <XAxis type="number" domain={[-bound, bound]} tick={axisTick(theme)} tickFormatter={fmtBy} />
              <YAxis dataKey="name" type="category" tick={axisTick(theme)} width={112} tickFormatter={cutName} />
              {/* 기준선 — 이게 없으면 좌우 분기의 기준점이 안 보인다 */}
              <ReferenceLine x={0} stroke={theme.palette.text.disabled} />
              <Tooltip
                {...tooltipProps()}
                cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
                content={<DeviationTooltip fmt={fmtBy} t={t} negColor={negColor} posColor={posColor} />}
              />
              <Bar dataKey="gapAmt" barSize={13} radius={[2, 2, 2, 2]} isAnimationActive={false}
                cursor="pointer" onClick={(d) => setSelectedName(d?.name ?? null)}>
                {rows.map((r) => (
                  // 선택된 막대만 진하게 — 어느 막대의 상세를 보고 있는지 좌우가 이어져야 한다
                  <Cell key={r.cd ?? r.name}
                    fill={(r.gapAmt ?? 0) < 0 ? negColor : posColor}
                    fillOpacity={selected && r.name !== selected.name ? 0.35 : 1} />
                ))}
                <LabelList dataKey="gapAmt"
                  content={<GapValueLabel fmt={fmtBy} negColor={negColor} posColor={posColor} />} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
        {/* 우: 선택 엔티티의 월별 계획·실적·달성률(디테일) */}
        <DeviationDetail row={selected} fmtBy={fmtBy} />
      </Box>
    </>
  );
};

/*
 * 계층 트리 필터 옵션 — 서버가 부모 먼저 평탄화해 준 `[{level, cd, name}]` 을 계단으로 보여 준다.
 *
 * value 가 `"{level}|{cd}"` 인 이유: 코드만으로는 어느 레벨의 노드인지 알 수 없고,
 * 서버는 `lv{n}_cd = ?` 로 거르므로 **레벨이 반드시 함께 와야** 한다. 이름은 중복될 수 있어 키로 못 쓴다.
 * 이름이 비면 코드로 폴백한다(dim 이 이름 없이 적재된 경우).
 */
const NODE_SEP = "|";
const treeOptions = (tree, allLabel) => [
  { value: "", label: allLabel, depth: 0 },
  ...(tree ?? []).map((n) => ({
    value: `${n.level}${NODE_SEP}${n.cd}`,
    label: n.name || n.cd,
    depth: n.level,   // lv1 도 "전체" 보다 한 칸 들어가야 하위임이 보인다
  })),
];
// "3|CD" → { node: "CD", lv: 3 }. 빈 값이면 전체(둘 다 undefined).
const parseNode = (v) => {
  if (!v) return { node: undefined, lv: undefined };
  const i = v.indexOf(NODE_SEP);
  return { node: v.slice(i + 1), lv: Number(v.slice(0, i)) };
};

// ─────────────────────────────────────────
// ⑤ 조합별 달성률 히트맵 — (거래처 레벨 × 품목 레벨) 조합당 1행 × 월 셀
// ─────────────────────────────────────────

/*
 * 왜 그리드가 아니라 히트맵인가 (2026-08-05 재설계)
 *
 * 처음엔 목업처럼 조합마다 measure 5행 × 16열 그리드였는데 **읽을 수가 없었다**:
 *   - 카드 높이에 조합 2개가 들어간다(5행 × N조합). 한 조합을 보려면 숫자 80개를 훑어야 한다.
 *   - ag-grid 의 rowSpan 병합 셀은 그룹 **첫 행**에만 렌더된다 → 세로 스크롤하면 거래처·품목
 *     라벨이 화면 밖으로 나가 "지금 뭘 보고 있는지" 모르게 된다. 병합의 구조적 한계다.
 *   - `₩859M` 같은 긴 포맷 × 80칸 = 숫자 벽.
 *
 * 이 카드가 답할 질문은 값 조회가 아니라 **"어디가 언제 틀어졌나"(이상 탐지)** 다.
 * → 5행을 1행으로 접고(달성률만), 숫자를 색으로 바꾼다. 같은 높이에 조합 10~12개가 들어가고
 *   빨간 블록이 어디 몰렸는지 스크롤 없이 잡힌다. 원본 5개 measure 는 **행을 펼치면** 나온다.
 *
 * ag-grid 를 안 쓴 이유: 셀 배경을 값에 따라 칠하고 행을 펼치는 건 CSS 그리드가 훨씬 단순하다
 * (rowSpan·cellStyle·detailRenderer 조합보다 코드가 짧고 병합 라벨 문제도 애초에 없다).
 */

const HEAT_ROW_H = 26;
const HEAT_HEAD_H = 34;

// 좌측 고정 블록(펼침 화살표·거래처·품목·기간 달성률·갭) + 월 셀 N개. 헤더와 각 행이 이 값을 공유해야 열이 맞는다.
const heatTemplate = (n) => `18px minmax(88px, 1.1fr) minmax(110px, 1.4fr) 58px 74px repeat(${n}, minmax(32px, 1fr))`;

// 월 헤더 — 연도는 바뀔 때만 위에 작게. "2025-08" 을 16번 반복하면 헤더가 숫자 벽이 된다.
const MonthHead = ({ ym, prevYm }) => {
  const [y, m] = ym.split("-");
  const showYear = !prevYm || prevYm.split("-")[0] !== y;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", px: 0.25 }}>
      <Typography sx={{ fontSize: FS.CAPTION, color: "text.disabled", lineHeight: 1.1, visibility: showYear ? "visible" : "hidden" }}>
        {y}
      </Typography>
      <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", lineHeight: 1.3 }}>{m}</Typography>
    </Box>
  );
};

// 달성률 셀 — 배경 = 구간 색, 텍스트 = 값. 값이 없으면(미래·미완료 월) 아무것도 칠하지 않는다.
//   ★ 0% 로 칠하면 "완전 미달"로 읽힌다. 빈 셀이어야 "아직 안 옴"이다.
const HeatCell = ({ v, tone }) => {
  const band = bandOf(v);
  const sx = band
    ? { bgcolor: alpha(tone[TONE_BY_BUCKET[band]].color, tone[TONE_BY_BUCKET[band]].alpha) }
    : {};
  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "center",
      borderRadius: 0.3, m: "1px", minWidth: 0, ...sx,
    }}>
      <Typography sx={{ fontSize: FS.CAPTION, color: band ? "text.primary" : "text.disabled", lineHeight: 1 }}>
        {band ? `${Math.round(v)}%` : ""}
      </Typography>
    </Box>
  );
};

// 펼침 패널 — 그 조합의 계획 4단계 vs 실적 추이. 히트맵이 버린 원본 숫자를 여기서 되돌려준다.
//   페이지 차트 설정(chartConfig)과 무관하게 항상 같은 모양이라 빈 config 를 넘긴다.
const HeatRowDetail = ({ row, months, fmtBy }) => {
  const { t } = useTranslation();
  const attainLabel = t("pvaAttainment");
  const SERIES = useMemo(() => planSeriesColors(), []);
  const data = useMemo(() => months.map((m) => ({
    period: m,
    ff: row.measures?.ff?.[m] ?? null,
    sf: row.measures?.sf?.[m] ?? null,
    mf: row.measures?.mf?.[m] ?? null,
    actual: row.measures?.actual?.[m] ?? null,
    attainment: row.measures?.attainment?.[m] ?? null,
  })), [row, months]);

  return (
    <Box sx={{ gridColumn: "1 / -1", height: 190, display: "flex", flexDirection: "column",
      px: 1, py: 0.5, bgcolor: "action.hover", borderRadius: 0.5 }}>
      <DashboardComposedChart
        data={data}
        yAxes={[
          { id: "val", tickFormatter: fmtBy },
          { id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` },
        ]}
        tooltipFormatter={(v, name) => (name === `${attainLabel} (%)` ? `${v}%` : fmtBy(v))}
      >
        {barOrLine({}, { key: "sf", dataKey: "sf", name: t("salesForecast"), color: SERIES.sf, yAxisId: "val", dflt: "BAR" })}
        {barOrLine({}, { key: "mf", dataKey: "mf", name: t("managerForecast"), color: SERIES.mf, yAxisId: "val", dflt: "BAR" })}
        {barOrLine({}, { key: "actual", dataKey: "actual", name: t("salesActual"), color: SERIES.actual, yAxisId: "val", dflt: "BAR" })}
        {barOrLine({}, { key: "ff", dataKey: "ff", name: t("financeForecast"), color: SERIES.ff, yAxisId: "val", dflt: "DASHED" })}
        {barOrLine({}, { key: "attainment", dataKey: "attainment", name: `${attainLabel} (%)`, color: SERIES.attainment, yAxisId: "pct", dflt: "LINE" })}
      </DashboardComposedChart>
    </Box>
  );
};

const AttainHeatmapCard = ({ title, grid, months, fmtBy, itemLevels, itemLevel, onChangeItemLevel,
  custLevels, custLevel, onChangeCustLevel }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tone = useMemo(() => toneColorOf(theme), [theme]);
  const [expanded, setExpanded] = useState(null);   // 펼친 행 index — 한 번에 하나만

  const rows = grid?.rows ?? [];
  const template = heatTemplate(months.length);

  const levelOptions = (levels, fallbackLabel) => (levels ?? []).map((lv) => ({
    value: String(lv.value),
    label: lv.label || lv.cd || `${fallbackLabel} ${lv.value}`,
  }));
  const itemOptions = levelOptions(itemLevels, t("level"));
  const custOptions = levelOptions(custLevels, t("level"));
  /*
   * 선택값이 옵션에 없으면 최하위 레벨로 떨어뜨린다.
   * 백엔드가 itemLevel/custLevel 을 안 내려주면 value 가 "" 가 되는데, MUI Select 는 옵션에 없는 값을
   * 받으면 out-of-range 경고를 내고 표시가 빈칸이 된다("레벨을 못 고르는" 것처럼 보인다).
   */
  const pickLevel = (options, value) =>
    (options.some((o) => o.value === String(value)) ? String(value) : options[options.length - 1]?.value ?? "");

  const headCellSx = { display: "flex", alignItems: "flex-end", fontSize: FS.CAPTION, color: "text.secondary", px: 0.5 };
  const textCellSx = { display: "flex", alignItems: "center", px: 0.5, minWidth: 0 };
  const numCellSx = { ...textCellSx, justifyContent: "flex-end" };

  // 요약(기간 달성률·갭)은 서버가 완료 월만으로 계산해 준다 — 프런트에서 다시 재지 않는다(정의가 갈리면 안 됨).
  const summaryOf = (r) => r?.summary ?? {};

  const renderRow = (r, i, isTotal) => {
    const s = summaryOf(r);
    const gap = s.gap ?? 0;
    /*
     * ❌ "같은 거래처가 이어지면 흐리게" 를 여기서 하지 말 것 (2026-08-07 제거).
     *   반복값 흐리기는 **그룹핑 관용구**인데 이 그리드엔 그룹이 없다 — 행은 |갭| 내림차순이라
     *   같은 거래처가 붙는 건 정렬상 우연이다. 그래서 같은 "Canada Sales" 가 3행은 회색, 8행은 검정으로
     *   나와 "왜 얘만 회색이냐"는 질문을 만들었다(사용자 지적). 아무도 안 쓰는 정보를 강조하면서
     *   있지도 않은 그룹 구조를 암시한다.
     *   → 이 카드에 **거래처순 정렬 옵션이 생기면** 그때는 되살릴 만하다(그때는 진짜 그룹이 된다).
     */
    const open = expanded === i;

    return (
      <Box
        key={isTotal ? "__total" : `${r.custName}|${r.itemName}|${i}`}
        onClick={isTotal ? undefined : () => setExpanded(open ? null : i)}
        sx={{
          display: "grid", gridTemplateColumns: template, alignItems: "stretch",
          minHeight: HEAT_ROW_H,
          borderBottom: `1px solid ${theme.palette.divider}`,
          ...(isTotal
            ? { position: "sticky", top: HEAT_HEAD_H, zIndex: 1, bgcolor: "background.paper", fontWeight: 700 }
            : { cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled" }}>
          {!isTotal && (open
            ? <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
            : <KeyboardArrowRightIcon sx={{ fontSize: 14 }} />)}
        </Box>
        <Box sx={textCellSx}>
          <Typography noWrap sx={{ fontSize: FS.BODY, fontWeight: isTotal ? 700 : 400, color: "text.primary" }}>
            {isTotal ? t("total") : r.custName}
          </Typography>
        </Box>
        <Box sx={textCellSx}>
          <Typography noWrap sx={{ fontSize: FS.BODY, color: "text.primary" }}>{isTotal ? "" : r.itemName}</Typography>
        </Box>
        <Box sx={numCellSx}>
          <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.primary" }}>
            {s.attainment != null ? `${s.attainment}%` : ""}
          </Typography>
        </Box>
        <Box sx={numCellSx}>
          {/* 부호 색은 발산형 스케일과 같게 — 미달=빨강, 초과=파랑(초록으로 쓰면 히트맵의 "정상"과 헷갈린다) */}
          <Typography noWrap sx={{ fontSize: FS.BODY, color: gap < 0 ? tone.under2.color : tone.over2.color }}>
            {s.gap != null ? `${gap > 0 ? "+" : ""}${fmtBy(gap)}` : ""}
          </Typography>
        </Box>
        {months.map((m) => (
          <HeatCell key={m} v={r.measures?.attainment?.[m]} tone={tone} />
        ))}
      </Box>
    );
  };

  return (
    <>
      <DashboardCardHeader title={title} dense>
        {/* 계층 집계 카드엔 레벨 드롭다운이 필수. 레벨이 1개뿐이면 고를 게 없으므로 숨긴다 */}
        {custOptions.length > 1 && (
          <CompactSelect value={pickLevel(custOptions, custLevel)} options={custOptions}
            onChange={onChangeCustLevel} minWidth={104} />
        )}
        {itemOptions.length > 1 && (
          <CompactSelect value={pickLevel(itemOptions, itemLevel)} options={itemOptions}
            onChange={onChangeItemLevel} minWidth={104} />
        )}
      </DashboardCardHeader>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {/* 헤더 — 스크롤해도 월이 보여야 한다 */}
        <Box sx={{
          display: "grid", gridTemplateColumns: template, height: HEAT_HEAD_H,
          position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Box />
          <Box sx={headCellSx}>{t("customer")}</Box>
          <Box sx={headCellSx}>{t("item")}</Box>
          <Box sx={{ ...headCellSx, justifyContent: "flex-end" }}>{t("pvaAttainment")}</Box>
          <Box sx={{ ...headCellSx, justifyContent: "flex-end" }}>{t("deviation")}</Box>
          {months.map((m, i) => <MonthHead key={m} ym={m} prevYm={i > 0 ? months[i - 1] : null} />)}
        </Box>

        {grid?.totalRow && renderRow(grid.totalRow, -1, true)}
        {rows.map((r, i) => (
          <Box key={`${r.custName}|${r.itemName}|${i}`}>
            {renderRow(r, i, false)}
            {expanded === i && (
              <Box sx={{ display: "grid", gridTemplateColumns: template }}>
                <HeatRowDetail row={r} months={months} fmtBy={fmtBy} />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* 범례 = 분포 카드(③)와 같은 구간·같은 색. 두 카드를 오가며 읽을 수 있어야 한다 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap", flexShrink: 0 }}>
        {DIST_BUCKETS.filter((b) => b.label).map((b) => (
          <Box key={b.key} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.3,
              bgcolor: alpha(tone[b.tone].color, tone[b.tone].alpha),
              border: `1px solid ${theme.palette.divider}` }} />
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{b.label}</Typography>
          </Box>
        ))}
        {/* 잘렸으면 반드시 알린다 — 조용한 절단은 "전부 보고 있다"는 오해를 만든다 */}
        {grid?.truncated && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", ml: "auto" }}>
            {t("pvaTruncated", { shown: grid.truncated.shown, total: grid.truncated.total })}
          </Typography>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const SalesPlanAccuracyDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();   // 권한 밖 화면으로 가는 드릴 링크는 만들지 않는다
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [unit, setUnit] = useState(reduxViewState?.unit ?? "AMT");                 // 페이지 단일 단위 토글
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");     // 품목 계층 집계 레벨
  const [custLevel, setCustLevel] = useState(reduxViewState?.custLevel ?? "");     // 거래처 계층 집계 레벨
  /*
   * 계층 트리 필터(페이지 전체). "" = 전체, 아니면 `"{level}|{cd}"`.
   * ★ 집계 레벨(itemLevel)과 **독립**이다 — 트리에서 고른 지점이 곧 필터 레벨이라,
   *   집계 레벨을 바꿔도 이 선택은 그대로 유효하다(초기화할 필요가 없다).
   */
  const [itemNodeSel, setItemNodeSel] = useState(reduxViewState?.itemNodeSel ?? "");
  const [custNodeSel, setCustNodeSel] = useState(reduxViewState?.custNodeSel ?? "");
  const [drillHoverKey, setDrillHoverKey] = useState(null);   // 제목 hover 중인 드릴 카드 key
  const [devAxis, setDevAxis] = useState(reduxViewState?.devAxis ?? "item");       // 편차 카드 축(품목/거래처)
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [configScope, setConfigScope] = useState(null);   // 톱니를 누른 카드 key — 모달은 그 카드의 measure 만 보여준다

  // 단위·축 전환은 재조회 불필요(AMT/QTY 를 함께 반환) — 집계 레벨만 재조회 트리거
  const { payload } = useDashboardData(salesPlanAccuracyService.get, {
    itemLevel: itemLevel || undefined,
    custLevel: custLevel || undefined,
    itemNode: parseNode(itemNodeSel).node,
    itemNodeLv: parseNode(itemNodeSel).lv,
    custNode: parseNode(custNodeSel).node,
    custNodeLv: parseNode(custNodeSel).lv,
  });

  // 사용자별 설정(z_p13n, pageCd = viewName) — 카드 레이아웃 + 차트 타입 + 편차 Top N
  const { chartConfig, topN, layout, saveChartConfig, saveTopN, saveLayout, toggleSeries } =
    useDashboardP13n(viewName, { defaultChartConfig: DEFAULT_CHART_CONFIG, defaultLayout: DEFAULT_LAYOUT });

  useEffect(() => {
    const next = reduxUtil.createObjectState(viewName, { unit, itemLevel, custLevel, itemNodeSel, custNodeSel, devAxis });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, itemLevel, custLevel, itemNodeSel, custNodeSel, devAxis]);

  const fmtMoney = useMemo(() => makeFmtMoney(payload?.currencyCd), [payload]);
  // 단위 토글이 KPI·차트·테이블의 포맷을 한꺼번에 바꾼다
  const fmtBy = useMemo(() => (unit === "AMT" ? fmtMoney : fmtQty), [unit, fmtMoney]);

  const cardLabels = useMemo(() => ({
    trend: t("pvaTrendCard"),
    stage: t("pvaStageCard"),
    dist: t("pvaDistCard"),
    deviation: t("pvaDeviationCard"),
    detail: t("pvaDetailCard"),
  }), [t]);

  // 설정 모달 measure — **카드 스코프**(톱니를 누른 카드의 시리즈만). 색은 차트와 같은 소스라 스와치가 어긋나지 않는다.
  const configMeasures = useMemo(() => ({
    trend: [
      { key: "ff", label: t("financeForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.ff) },
      { key: "sf", label: t("salesForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.sf) },
      { key: "mf", label: t("managerForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.mf) },
      { key: "actual", label: t("salesActual"), hasActive: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.actual) },
      { key: "attainment", label: t("pvaAttainment"), hasActive: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.attainment) },
    ],
    stage: [
      { key: "ffAcc", label: t("financeForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.ff) },
      { key: "sfAcc", label: t("salesForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.sf) },
      { key: "mfAcc", label: t("managerForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.mf) },
      { key: "bfAcc", label: t("baselineForecast"), hasActive: true, color: ColorUtil.colorSet(STAGE_COLOR_IDX.bf) },
    ],
  }[configScope] ?? []), [t, configScope]);

  /*
   * Plan vs Actual Trend 카드 → DP Plan vs. Actual (§9-1 표준: 제목만 클릭 영역).
   *
   * 넘기는 것: **차트 창(16개월)** + 월 버킷 + **현재 계층 필터**.
   *   · 기간은 KPI 창(12개월)이 아니라 차트 창이다 — 사용자가 그 카드에서 보고 있는 구간이어야 한다.
   *   · 버전은 안 넘긴다 → 대상이 최신 RELEASED 를 자동 선택(§9 규칙).
   *   · 계층은 **이어받는다**. dim 의 `lv{n}_cd` 는 `z_customer_hrchy.hrchy_cd` 그 자체이고,
   *     `configCd`(= z_hrchy_config.cd)가 대상이 읽는 `hrchyLv` 다. 코드 공간이 같아 그대로 통한다.
   *   · 대상 키 이름 주의: 거래처는 `filter.customerHrchys`, **품목은 `filter.category`** 다(itemHrchys 아님).
   */
  const nodeFor = (sel, tree) => {
    const { node, lv } = parseNode(sel);
    if (!node) return null;
    const found = (tree ?? []).find((n) => n.level === lv && n.cd === node);
    // 대상이 읽는 모양 그대로: hrchyCd(필터값) + hrchyLv(레벨 코드) + name(필터 칩 표시용)
    return [{ hrchyCd: node, hrchyLv: found?.configCd ?? null, name: found?.name ?? node }];
  };

  const openDpPlanVsActual = () => {
    const target = "DP_ACTUAL_PLAN";
    const p = payload?.chartPeriod;
    if (!p?.from || !p?.to) return;
    const [fy, fm] = p.from.split("-").map(Number);
    const [ty, tm] = p.to.split("-").map(Number);
    reduxDispatch(reduxUtil.removeViewState({ viewName: target }));
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(target, {
      startDate: new ZDate(fy, fm - 1, 1, 0, 0, 0),
      endDate: new ZDate(ty, tm, 0, 23, 59, 59),
      timeDimension: codeUtil.buckets.MONTH,
      filter: {
        customerHrchys: nodeFor(custNodeSel, payload?.custTree),
        category: nodeFor(itemNodeSel, payload?.itemTree),
      },
      customerFilterList: {},
      itemFilterList: {},
    })));
    reduxDispatch(reduxUtil.updateHistoryState(target));
  };

  // 편차 Top N 슬라이스 — 서버는 상위 10까지 주고 프런트가 자른다(재조회 없이 전환)
  const shownDeviation = useMemo(() => {
    const src = payload?.deviationTop?.[unit];
    const rows = devAxis === "item" ? src?.byItem : src?.byCustomer;
    return (rows ?? []).slice(0, topN);
  }, [payload, unit, devAxis, topN]);

  if (!payload) return null;

  const kpis = payload.kpis ?? {};
  const months = payload.detailGrid?.months ?? [];

  /*
   * 계획 커버리지 = 계획이 덮은 매출 비중. 비교 지표(달성률·정확도·편향·갭)는 전부 **계획이 있는 셀만**
   * 집계하므로, 이 값이 낮으면 그 지표들은 매출의 극히 일부만 본 결과다.
   * 임계값 50%: 절반도 못 덮으면 "표본"이라 부르기 어렵다 → 경고색으로 눈에 띄게 한다.
   */
  const coverage = kpis.attainment?.[unit]?.coverage ?? 0;
  const lowCoverage = coverage < 50;

  // 카드 key → 렌더 함수
  const renderCard = (key) => {
    switch (key) {
      case "trend":
        return <TrendCard title={cardLabels.trend} data={payload.trend?.[unit] ?? []} fmtBy={fmtBy}
          chartConfig={chartConfig} onOpenConfig={() => setConfigScope("trend")} onToggleSeries={toggleSeries}
          onDrill={isPermitted("DP_ACTUAL_PLAN") ? openDpPlanVsActual : undefined}
          onDrillHover={(on) => setDrillHoverKey(on ? "trend" : null)} />;
      case "stage":
        return <StageAccuracyCard title={cardLabels.stage} data={payload.accuracyByStage?.[unit] ?? []} unit={unit}
          chartConfig={chartConfig} onOpenConfig={() => setConfigScope("stage")} onToggleSeries={toggleSeries} />;
      case "dist":
        return <AttainDistCard title={cardLabels.dist} dist={payload.attainDist?.[unit]} fmtBy={fmtBy} />;
      case "deviation":
        return <DeviationChart title={cardLabels.deviation} rows={shownDeviation} axis={devAxis} onChangeAxis={setDevAxis}
          fmtBy={fmtBy} topN={topN} onChangeTopN={saveTopN} />;
      case "detail":
        return <AttainHeatmapCard title={cardLabels.detail} grid={payload.detailGrid?.[unit]} months={months}
          fmtBy={fmtBy}
          itemLevels={payload.itemLevels} itemLevel={itemLevel || payload.itemLevel} onChangeItemLevel={setItemLevel}
          custLevels={payload.custLevels} custLevel={custLevel || payload.custLevel} onChangeCustLevel={setCustLevel} />;
      default:
        return null;
    }
  };

  const spanOf = (key) => CARD_DEFS.find((c) => c.key === key)?.span ?? 3;
  const minHOf = (key) => CARD_DEFS.find((c) => c.key === key)?.minH ?? 260;
  const visibleOrder = (layout?.order ?? DEFAULT_LAYOUT.order).filter((k) => !layout?.hidden?.[k]);

  // 6열 기준으로 카드를 실제 행으로 묶는다(span 누적, 6 초과 시 다음 행).
  const gridRows = visibleOrder.reduce((rows, key) => {
    const s = spanOf(key);
    const last = rows[rows.length - 1];
    if (!last || last.used + s > 6) rows.push({ keys: [key], used: s });
    else { last.keys.push(key); last.used += s; }
    return rows;
  }, []);
  // 각 행 = minmax(최소높이, 최소높이fr): 세로 여백이 남으면 fr 가 비례로 채워 흰 여백 제거,
  // 모자라면 하한으로 스크롤.
  const gridTemplateRows = gridRows
    .map((r) => { const m = Math.max(...r.keys.map(minHOf)); return `minmax(${m}px, ${m}fr)`; })
    .join(" ");

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        {/*
          * 비교 기준(MF)을 화면에 한 번만 명시한다 — 카드마다 붙이면 시끄럽고, 없으면 "어느 계획?"이 매번 나온다.
          * 계획 이름은 t("managerForecast") 재사용 — Sales Status 차트 범례와 같은 문구여야 화면 간 말이 맞는다.
          * 예외는 Accuracy by Plan Stage 카드 한 장(FF/SF/MF/BF 비교)뿐이고, 그건 제목이 스스로 밝힌다.
          */}
        <Typography sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
          {t("pvaBasis", { plan: t("managerForecast") })}
        </Typography>
        {/*
          * 계층 노드 필터 — **페이지 전체**에 걸린다(KPI·추이·분포·편차·히트맵 모두 같은 범위).
          * 카드 하나만 거르면 "추이는 일본, KPI는 전체"가 되어 화면이 서로 다른 말을 한다.
          * 목록은 **계층 트리 전체**를 계단으로 보여 준다 — 사용자가 원하는 깊이의 노드를 직접 고르고,
          * 고른 지점이 곧 필터 레벨이다(집계 레벨과 무관).
          */}
        <CompactSelect displayEmpty value={custNodeSel} options={treeOptions(payload.custTree, t("all"))}
          onChange={setCustNodeSel} minWidth={130} />
        <CompactSelect displayEmpty value={itemNodeSel} options={treeOptions(payload.itemTree, t("all"))}
          onChange={setItemNodeSel} minWidth={130} />
        <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnit} />
        <IconButton size="small" onClick={() => setLayoutOpen(true)} sx={{ p: 0.4, color: "text.secondary" }}>
          <TuneIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </DashboardTopBar>

      {/*
       * KPI 4장 — 서로 다른 질문에 답한다.
       *   달성률: 계획한 만큼 팔았나 (상쇄된다)
       *   정확도: 조합 단위로 얼마나 틀렸나 (방향이 없다)
       *   편향  : 상습 과잉인가 과소인가 (규모를 모른다)
       *   갭    : 그래서 얼마짜리 문제인가
       * 정확도·편향의 델타는 pp(퍼센트 포인트) — 비율의 증감을 % 로 쓰면 반드시 오해한다.
       */}
      <DashboardRow kpi columns={4}>
        {/*
          * 계획 커버리지를 달성률과 **같은 카드**에 붙인다 — 따로 두면 안 본다.
          * 커버리지 1% 짜리 달성률 131% 는 "계획대로 됐다"가 아니라 "1% 만 재 봤다" 는 뜻이고,
          * 그걸 모르면 이 화면의 모든 숫자를 오해한다(실측 zenbike 에서 실제로 그랬다).
          */}
        <KpiCard label={t("pvaAttainment")} value={`${kpis.attainment?.[unit]?.value ?? 0}%`}
          deltaPct={kpis.attainment?.[unit]?.deltaPct}
          sub={<>
            {`${t("pvaCoverage")} `}
            <Box component="span" sx={{
              color: lowCoverage ? "warning.main" : "text.secondary",
              fontWeight: lowCoverage ? 700 : 400,
            }}>
              {`${coverage}%`}
            </Box>
          </>}
          sparkData={payload.spark?.attainment?.[unit]} gradientId="sparkPvaAttainment" />
        <KpiCard label={t("pvaAccuracy")} value={`${kpis.accuracy?.[unit]?.value ?? 0}%`}
          deltaPct={kpis.accuracy?.[unit]?.deltaPp} deltaUnit="pp"
          sparkData={payload.spark?.accuracy?.[unit]} gradientId="sparkPvaAccuracy" />
        {/*
          * 편향만 `deltaGood` 을 넘긴다 — 0 에 가까울수록 좋은 지표라 부호로는 좋고 나쁨을 못 가린다.
          * 서버가 |당기| < |전기| 를 계산해 `improved` 로 준다(비교 불가면 null → 부호 기준으로 폴백).
          */}
        <KpiCard label={t("pvaBias")} value={`${kpis.bias?.[unit]?.value ?? 0}%`}
          deltaPct={kpis.bias?.[unit]?.deltaPp} deltaUnit="pp"
          deltaGood={kpis.bias?.[unit]?.improved ?? undefined}
          sub={(kpis.bias?.[unit]?.value ?? 0) >= 0 ? t("pvaBiasOver") : t("pvaBiasUnder")}
          sparkData={payload.spark?.bias?.[unit]} gradientId="sparkPvaBias" />
        {/* 갭은 항상 금액이라 sub 의 달성률도 금액 기준 — 단위 토글이 QTY 일 때 좌측 카드와 값이 달라 보이므로 명시한다 */}
        <KpiCard label={t("pvaGap")} value={fmtMoney(kpis.gap?.amt ?? 0)} deltaPct={kpis.gap?.deltaPct}
          sub={`${t("pvaAttainment")} ${kpis.gap?.attainment ?? 0}% (${t("amt")})`}
          sparkData={payload.spark?.gap} gradientId="sparkPvaGap" />
      </DashboardRow>

      <Box sx={{
        flex: 1, minHeight: 0, overflow: "auto",
        display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows,
        gridAutoFlow: "row dense", gap: 1.5,   // 행 트랙(fr)이 남는 세로를 채워 하단 흰 여백 제거
      }}>
        {visibleOrder.map((key) => (
          <Card key={key} sx={{
            gridColumn: `span ${spanOf(key)}`, minHeight: 0,
            transition: CARD_HOVER_TRANSITION,
            ...(drillHoverKey === key && CARD_HOVER_SX),
          }}>
            {renderCard(key)}
          </Card>
        ))}
      </Box>

      <DashboardLayoutModal
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        cards={CARD_DEFS.map((c) => ({ key: c.key, label: cardLabels[c.key] }))}
        layout={layout}
        onSave={(next) => { saveLayout(next); setLayoutOpen(false); }}
      />

      <ChartConfigModal
        open={configScope != null}
        onClose={() => setConfigScope(null)}
        config={chartConfig}
        onSave={(cfg) => { saveChartConfig(cfg); setConfigScope(null); }}
        measures={configMeasures}
        chartTypes={DW_CHART_TYPES}
      />
    </DashboardPage>
  );
};

export default SalesPlanAccuracyDashboard;
